// Shared duration formatting for anything that shows a learner how long they
// have spoken. It lives here rather than in a view because two surfaces now
// need the same rounding: the Progress page's Voice Ledger rows and the
// lesson-completion sheet's two stat tiles. Two copies of this arithmetic
// would eventually disagree, and the sheet's old formatter already did — it
// wrote "2 hours, 4 minutes, 31 seconds" in hardcoded English in all 31
// locales.
//
// Minutes under an hour, hours and minutes above it. Returns the key and its
// params rather than a string so every range stays a translation, and so the
// rounding is testable without a locale.
export function durationParts(seconds) {
  const value = Number(seconds);
  const totalMinutes = Math.floor(Math.max(0, Number.isFinite(value) ? value : 0) / 60);
  if (totalMinutes < 60) {
    return { key: "voiceLedger.minutesShort", params: { minutes: totalMinutes } };
  }
  return {
    key: "voiceLedger.hoursMinutes",
    params: { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 },
  };
}

// The lesson-completion sheet's variant. `durationParts` floors to whole
// minutes, which is right for the Progress page (a whole minute of practice is
// the smallest unit that page shows, by design) but wrong beside it on the
// completion sheet: a 45-second first lesson would read "0 min" in the lesson
// tile while the all-time tile read "0 min" too, and rounding the lesson tile
// up to "1 min" instead would contradict a genuinely-zero all-time total.
//
// So a value strictly between zero and a minute gets its own key, and
// everything else defers to `durationParts` unchanged. Exactly zero stays the
// honest "0 min". BOTH tiles go through this, so the two sides of the sheet can
// never round the same number two different ways.
export function spokenParts(seconds) {
  const value = Number(seconds);
  if (Number.isFinite(value) && value > 0 && value < 60) {
    return {
      key: "voiceLedger.secondsShort",
      // Floored, so 59.9 stays "59 s" rather than jumping to a "60 s" this
      // range should never print, but never below one: the branch is already
      // known to hold a value greater than zero, and telling a learner who
      // spoke for half a second that they spoke "0 s" would be a worse lie
      // than the honest "0 min" that exactly zero gets.
      params: { seconds: Math.max(1, Math.floor(value)) },
    };
  }
  return durationParts(seconds);
}
